import { useQuery } from '@tanstack/react-query';
import type { OnboardingNudgePage } from '@wundo/shared/onboardingNudge.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchOnboardingNudge, onboardingNudgeKey } from './onboardingNudge.ts';
import type { ChatPageNudge } from './chatPageOffer.ts';

/**
 * What this page recommends, for the offer it publishes to the drawer.
 *
 * Only the five pages that own a nudge slot ask; everywhere else the drawer is
 * simply the drawer. Somebody past their first four weeks — which is almost
 * everybody — gets nothing back, and nothing is drawn.
 *
 * The current user is read from the same cache every page reads it from, so
 * this is one request per page and not two.
 */
export function useChatPageNudge(page: OnboardingNudgePage): ChatPageNudge | undefined {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data } = useQuery({
    queryKey: onboardingNudgeKey(user?.id, page),
    queryFn: async () => await fetchOnboardingNudge(user!.id, page),
    enabled: Boolean(user?.id),
    /*
     * Asked again on every visit: the answer turns on work that may have been
     * done between two page views, and it is one cheap read.
     */
    staleTime: 0,
  });

  const nudge = data?.nudge;
  if (!nudge) return undefined;

  return {
    key: nudge.key,
    ...(nudge.meeting_id ? { meetingId: nudge.meeting_id } : {}),
    firstTime: nudge.first_time,
  };
}
